import React, { useEffect, useState, useRef } from "react";
import { Building2, ChevronDown, Check } from "lucide-react";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";

/**
 * HQ Branch-scope selector — visible ONLY to users whose primary branch is
 * the HQ (Maputo) distributor AND who have cross-branch visibility
 * (all_branches=true). Everyone else is already restricted to their own
 * branch by the backend; showing this would be noise.
 *
 * Options:
 *   - hq        → "Só Maputo"  (DEFAULT when nothing stored)
 *   - dealers   → "Só Dealers"
 *   - all       → "Tudo"
 *   - <name>    → specific branch (one of the dealers)
 *
 * Persistence: localStorage key "rcc_branch_scope". The axios interceptor in
 * /app/frontend/src/lib/api.js reads this key and appends ?branch_scope=…
 * to every GET so every list endpoint honours it automatically.
 */
const STORAGE_KEY = "rcc_branch_scope";

export function readScope() {
  try { return window.localStorage.getItem(STORAGE_KEY) || "hq"; } catch { return "hq"; }
}

export function writeScope(v) {
  try {
    if (!v || v === "hq") window.localStorage.setItem(STORAGE_KEY, "hq");
    else window.localStorage.setItem(STORAGE_KEY, v);
    // Notify listeners in the same tab (storage event doesn't fire for same window)
    window.dispatchEvent(new CustomEvent("rcc:branch-scope-changed", { detail: { scope: v } }));
  } catch {}
}

export default function BranchScopeSelector() {
  const { user } = useAuth();
  const [hqBranch, setHqBranch] = useState("Maputo");
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState(() => readScope());
  const popRef = useRef(null);

  // Fetch available branches + HQ name once (admins / HQ users only reach here).
  useEffect(() => {
    let alive = true;
    api.get("/branches/hq").then((r) => { if (alive) setHqBranch(r.data?.hq_branch || "Maputo"); }).catch(() => {});
    api.get("/branches").then((r) => {
      if (!alive) return;
      const list = Array.isArray(r.data) ? r.data.map((b) => b.name).filter(Boolean) : [];
      // Hide migration artifacts (TESTBRANCH / TESTBR / TEST_*) from the selector.
      setBranches(list.filter((n) => !/^TEST(BRANCH|BR|_)/i.test(n)));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Only show for HQ users: primary branch = HQ AND all_branches=true.
  // all_branches=false (admin revoked) means the user shouldn't be broadening
  // the view — hide the selector entirely.
  if (!user) return null;
  const isHqUser = (user.branch === hqBranch) && user.all_branches;
  if (!isHqUser) return null;

  const dealers = branches.filter((b) => b !== hqBranch);
  const current = scope || "hq";
  const label =
    current === "all"       ? "Tudo (Maputo + Dealers)" :
    current === "dealers"   ? "Só Dealers" :
    current === "hq"        ? `Só ${hqBranch}` :
    `Filial: ${current}`;

  const choose = (v) => {
    setScope(v);
    writeScope(v);
    setOpen(false);
    // Force a full reload so every page's in-flight queries re-fire with the
    // new scope. Avoids subtle stale-cache bugs across modules.
    window.location.reload();
  };

  const Item = ({ value, children }) => (
    <button
      type="button"
      onClick={() => choose(value)}
      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-ronil-navy-50 ${current === value ? "text-ronil-navy font-semibold" : "text-slate-700"}`}
      data-testid={`branch-scope-option-${value}`}
    >
      <Check className={`w-3.5 h-3.5 ${current === value ? "text-ronil-navy" : "opacity-0"}`} />
      {children}
    </button>
  );

  return (
    <div ref={popRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="h-9 pl-2.5 pr-2 flex items-center gap-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-sm text-slate-700 shadow-sm"
        data-testid="branch-scope-trigger"
        title="Alcance de visualização"
      >
        <Building2 className="w-4 h-4 text-ronil-navy" />
        <span className="hidden sm:inline max-w-[160px] truncate">{label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-md shadow-lg z-50 overflow-hidden" data-testid="branch-scope-menu">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 font-heading font-semibold border-b border-slate-100 bg-slate-50">
            Alcance de visualização
          </div>
          <Item value="hq">Só {hqBranch}</Item>
          <Item value="dealers">Só Dealers</Item>
          <Item value="all">Tudo (Maputo + Dealers)</Item>
          {dealers.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[9px] uppercase tracking-widest text-slate-400 font-heading font-semibold border-t border-slate-100">
                Filial específica
              </div>
              <div className="max-h-56 overflow-y-auto">
                {dealers.map((b) => (
                  <Item key={b} value={b}>{b}</Item>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
